import React from "react";
import {
  BrainCircuit, Users, Clock, Zap, Sparkles, AlertCircle, Square,
  Workflow, Check, ChevronRight, TrendingDown, Activity, CalendarDays, Info,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../../contexts/AuthContext";
import { useDashTheme } from "../hooks/useDashTheme";
import { useAiStream } from "../hooks/useAiStream";
import { PageShell } from "../components/PageShell";
import { SectionCard, Badge, Avatar, Button } from "../components/primitives";
import { EmptyState } from "../components/DataTable";
import { DateRangeTool, presetToRange, rangeForPreset } from "../components/DateRange";
import { ToolSelect } from "../components/ToolSelect";
import AILimitModal from "../../../components/AILimitModal/AILimitModal";
import { SERVER_URL } from "../../../utils/serverConfig";
import { useSurface, canvasStyle, ambientStyle } from "./growthSurface";

const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// InfoTip — the small "i" on every card. Hover or tap to read what the card
// means and where its numbers come from. Resets typography so it reads normally
// even inside uppercase KPI labels.
// ─────────────────────────────────────────────────────────────────────────────
function InfoTip({ text, side = "bottomLeft" }) {
  const t = useDashTheme();
  const [open, setOpen] = React.useState(false);
  const anchor = side === "bottomRight" ? { right: -4 }
    : side === "bottom" ? { left: "50%", transform: "translateX(-50%)" }
    : { left: -4 };
  return (
    <span style={{ position: "relative", display: "inline-flex", marginLeft: 5, verticalAlign: "middle" }}
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" aria-label="What is this?"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        style={{
          border: "none", background: "transparent", padding: 0, cursor: "help",
          display: "inline-flex", color: t.c.textTertiary, lineHeight: 0,
        }}>
        <Info size={13} />
      </button>
      {open && (
        <span role="tooltip" style={{
          position: "absolute", top: "calc(100% + 8px)", ...anchor,
          width: 250, zIndex: 60, background: t.c.surface, color: t.c.textSecondary,
          border: `1px solid ${t.c.border}`, borderRadius: t.r.md, boxShadow: t.shadow.lg,
          padding: "9px 11px", fontSize: 11.5, fontWeight: 400, lineHeight: 1.55,
          textAlign: "left", textTransform: "none", letterSpacing: 0, whiteSpace: "normal",
        }}>{text}</span>
      )}
    </span>
  );
}

// Severity → color. Red = close to the sun = act now.
const sevColor = (s) => (s >= 80 ? "#ef4444" : s >= 60 ? "#f97316" : s >= 40 ? "#eab308" : "#10b981");
const sevLabel = (s) => (s >= 80 ? "Act now" : s >= 60 ? "This week" : s >= 40 ? "Monitor" : "Minor");

// ─────────────────────────────────────────────────────────────────────────────
// SunCore — the animated sun, shared by the hero, the scanning state and the
// blocker map. SMIL rotation: CSS/framer transforms on SVG groups are
// unreliable across browsers, native animateTransform always works.
// ─────────────────────────────────────────────────────────────────────────────
function SunDefs() {
  return (
    <defs>
      <radialGradient id="cbSunCore" cx="38%" cy="34%" r="72%">
        <stop offset="0%" stopColor="#fde68a" />
        <stop offset="38%" stopColor="#fbbf24" />
        <stop offset="72%" stopColor="#f59e0b" />
        <stop offset="100%" stopColor="#ea580c" />
      </radialGradient>
      <radialGradient id="cbSunGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.5" />
        <stop offset="55%" stopColor="#f97316" stopOpacity="0.16" />
        <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="cbCalmCore" cx="38%" cy="34%" r="72%">
        <stop offset="0%" stopColor="#bbf7d0" />
        <stop offset="45%" stopColor="#34d399" />
        <stop offset="100%" stopColor="#059669" />
      </radialGradient>
      <radialGradient id="cbCalmGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
        <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="cbFlare" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fef3c7" stopOpacity="0.95" />
        <stop offset="60%" stopColor="#fbbf24" stopOpacity="0.35" />
        <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
      </linearGradient>
      <filter id="cbSoft" x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="2.2" />
      </filter>
    </defs>
  );
}

function Spin({ c, dur, reverse = false, children }) {
  return (
    <g>
      <animateTransform attributeName="transform" type="rotate"
        from={`${reverse ? 360 : 0} ${c} ${c}`} to={`${reverse ? 0 : 360} ${c} ${c}`}
        dur={`${dur}s`} repeatCount="indefinite" />
      {children}
    </g>
  );
}

function SunCore({ cx, cy, coreR = 44, label = ["COMPANY", "CORE"], streaming = false, calm = false }) {
  const c = cx; const core = calm ? "url(#cbCalmCore)" : "url(#cbSunCore)"; const glow = calm ? "url(#cbCalmGlow)" : "url(#cbSunGlow)";
  return (
    <g transform={cy !== cx ? `translate(0 ${cy - cx})` : undefined}>
      <circle cx={c} cy={c} r={coreR * 2.2} fill={glow}>
        <animate attributeName="opacity" values={streaming ? "0.55;1;0.55" : "0.7;1;0.7"} dur="2.6s" repeatCount="indefinite" />
        <animate attributeName="r" values={`${coreR * 2.05};${coreR * 2.35};${coreR * 2.05}`} dur="2.6s" repeatCount="indefinite" />
      </circle>
      {!calm && (
        <Spin c={c} dur={14}>
          <circle cx={c} cy={c} r={coreR + 10} fill="none" stroke="url(#cbFlare)" strokeWidth={2.6}
            strokeDasharray="28 54" strokeLinecap="round" opacity={0.9} />
        </Spin>
      )}
      <Spin c={c} dur={26} reverse>
        <circle cx={c} cy={c} r={coreR + 19} fill="none" stroke={calm ? "#34d399" : "#f59e0b"} strokeWidth={0.9}
          strokeDasharray="2 9" opacity={0.55} />
      </Spin>
      <circle cx={c} cy={c} r={coreR} fill={core} />
      <circle cx={c} cy={c} r={coreR} fill="none" stroke="#fff" strokeOpacity={0.25} strokeWidth={1} />
      {label.map((line, i) => (
        <text key={line} x={c} y={c + (i - (label.length - 1) / 2) * 14 + 4} textAnchor="middle"
          fontSize={10.5} fontWeight={800} letterSpacing="0.14em" fill="#fff" style={{ pointerEvents: "none" }}>
          {line}
        </text>
      ))}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HeroSun — landing state before the first analysis.
// ─────────────────────────────────────────────────────────────────────────────
const HERO_GHOSTS = [
  { orbit: 108, size: 10, dur: 22, start: 40, color: "#ef4444" },
  { orbit: 150, size: 8, dur: 34, start: 160, color: "#f97316" },
  { orbit: 150, size: 6, dur: 34, start: 300, color: "#eab308" },
  { orbit: 192, size: 7, dur: 48, start: 220, color: "#10b981" },
];

function HeroSun({ onAnalyze }) {
  const t = useDashTheme();
  const S = 440; const c = S / 2;
  return (
    <div style={{
      position: "relative", borderRadius: t.r.xl, border: `1px solid ${t.c.border}`,
      background: `radial-gradient(ellipse 60% 55% at 50% 38%, ${t.c.surfaceMuted} 0%, ${t.c.surface} 70%)`,
      overflow: "hidden", display: "flex", flexDirection: "column", alignItems: "center",
      padding: `${t.s[5]}px ${t.s[5]}px ${t.s[7]}px`,
    }}>
      {[...Array(18)].map((_, i) => (
        <motion.span key={i}
          animate={{ opacity: [0.1, 0.7, 0.1] }}
          transition={{ duration: 2 + (i % 5), repeat: Infinity, delay: i * 0.35 }}
          style={{
            position: "absolute", width: 2, height: 2, borderRadius: 99, background: t.c.textTertiary,
            left: `${(i * 53) % 96 + 2}%`, top: `${(i * 37) % 88 + 4}%`, pointerEvents: "none",
          }} />
      ))}
      <svg viewBox={`0 0 ${S} ${S}`} style={{ width: "min(430px, 92%)", height: "auto", display: "block" }}>
        <SunDefs />
        {HERO_GHOSTS.map((g, i) => (
          <g key={i}>
            <circle cx={c} cy={c} r={g.orbit} fill="none" stroke={t.c.border} strokeWidth={1} strokeDasharray="3 7" opacity={0.75} />
            <g transform={`rotate(${g.start} ${c} ${c})`}>
              <Spin c={c} dur={g.dur} reverse={i % 2 === 1}>
                <circle cx={c + g.orbit} cy={c} r={g.size} fill={g.color} opacity={0.9}
                  style={{ filter: `drop-shadow(0 0 6px ${g.color}88)` }} />
              </Spin>
            </g>
          </g>
        ))}
        <SunCore cx={c} cy={c} coreR={56} />
      </svg>
      <div style={{ textAlign: "center", maxWidth: 540, marginTop: -6, position: "relative" }}>
        <div style={{ fontSize: 20, fontWeight: 750, letterSpacing: "-0.02em", color: t.c.text }}>
          See what's blocking your company
        </div>
        <p style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.6, margin: `${t.s[2]}px 0 0` }}>
          Cross-team evidence is verified into blockers — a failure hit again and again, a wait on
          someone else, work stuck for days. Only what recurs on 2+ days makes the map, and a clean
          week says so plainly.
        </p>
        <div style={{ display: "flex", gap: t.s[2], justifyContent: "center", marginTop: t.s[5], flexWrap: "wrap" }}>
          <Button variant="primary" leftIcon={<Zap size={15} />} onClick={() => onAnalyze("7d")}>
            Analyze last 7 days
          </Button>
          <Button variant="secondary" onClick={() => onAnalyze("30d")}>Last 30 days</Button>
          <Button variant="secondary" onClick={() => onAnalyze(null)}>Use current filters</Button>
        </div>
        <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: t.s[3] }}>
          Scope with the filters above — entire company, one person, or a person plus their team. Reports are cached.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BlockerSun — the map. Every planet is a VERIFIED blocker; distance from the
// core is urgency, size is the hours caught in it. Labels are readable cards,
// not truncated strings. Zero blockers renders a calm green core instead of an
// artificially populated map.
// ─────────────────────────────────────────────────────────────────────────────
const SUN_W = 720; const SUN_H = 620; const SUN_CX = SUN_W / 2; const SUN_CY = 300;
const R_MIN = 132; const R_MAX = 252;

function sunNodes(blockers) {
  const sorted = [...(blockers || [])].sort((a, b) => b.severity - a.severity);
  // Interleave severities so neighbouring planets sit on different orbits.
  const order = [];
  const half = Math.ceil(sorted.length / 2);
  for (let i = 0; i < half; i++) {
    order.push(sorted[i]);
    if (sorted[i + half]) order.push(sorted[i + half]);
  }
  const n = order.length || 1;
  return order.map((b, i) => {
    const angle = ((i * (360 / n) - 90 + 360 / n / 2) * Math.PI) / 180;
    const r = R_MIN + (1 - b.severity / 100) * (R_MAX - R_MIN);
    const size = 14 + Math.min(12, (b.hoursCaught || 1) * 1.4);
    const x = SUN_CX + r * Math.cos(angle);
    const y = SUN_CY + r * Math.sin(angle);
    const lr = r + size + 56;
    return {
      ...b, x, y, size,
      lx: SUN_CX + lr * Math.cos(angle),
      ly: SUN_CY + lr * Math.sin(angle),
    };
  });
}

function BlockerSun({ blockers, selectedId, onSelect, streaming, allClear, analysed }) {
  const t = useDashTheme();
  const nodes = React.useMemo(() => sunNodes(blockers), [blockers]);

  if (allClear && !streaming) {
    return (
      <div style={{ textAlign: "center", padding: `${t.s[4]}px 0 ${t.s[3]}px` }}>
        <svg viewBox="0 0 720 340" style={{ width: "min(520px, 100%)", height: "auto", display: "block", margin: "0 auto" }}>
          <SunDefs />
          <circle cx={360} cy={170} r={118} fill="none" stroke={t.c.border} strokeWidth={1} strokeDasharray="3 8" opacity={0.8} />
          <SunCore cx={360} cy={170} coreR={58} calm label={["ALL", "CLEAR"]} />
        </svg>
        <div style={{ fontSize: t.font.size.md, fontWeight: 700, color: "#10b981", marginTop: 2 }}>No blockers this week</div>
        <p style={{ fontSize: t.font.size.xs, color: t.c.textSecondary, lineHeight: 1.6, margin: "4px auto 0", maxWidth: 460 }}>
          {analysed?.evidence ? `${analysed.evidence.toLocaleString()} pieces of tracked evidence analysed — ` : ""}
          nothing recurred enough to count as a blocker. We only show what we can prove,
          so a calm sun means a clean week, not missing analysis.
        </p>
      </div>
    );
  }

  if (!nodes.length) {
    return (
      <div style={{ position: "relative", minHeight: 380 }}>
        <svg viewBox="0 0 400 300" style={{ width: "100%", height: "auto" }}>
          <SunDefs />
          <SunCore cx={200} cy={150} coreR={44} streaming label={["SCANNING"]} />
        </svg>
        <div style={{ textAlign: "center", marginTop: -8 }}>
          <div style={{ fontSize: t.font.size.md, fontWeight: 600, color: t.c.text }}>Verifying blockers…</div>
          <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 2 }}>
            A blocker needs the same subject recurring on 2+ separate days
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${SUN_W} ${SUN_H}`} style={{ width: "100%", height: "auto", display: "block" }}>
        <SunDefs />
        <circle cx={SUN_CX} cy={SUN_CY} r={R_MAX + 34} fill="url(#cbSunGlow)" opacity={0.14} />
        {[R_MIN, (R_MIN + R_MAX) / 2, R_MAX].map((r, i) => (
          <Spin key={r} c={SUN_CX} dur={160 + i * 60} reverse={i % 2 === 1}>
            <circle cx={SUN_CX} cy={SUN_CY} r={r} fill="none" stroke={t.c.border} strokeWidth={1}
              strokeDasharray="3 7" opacity={0.9 - i * 0.22} />
          </Spin>
        ))}
        <text x={SUN_CX} y={SUN_CY - R_MIN + 18} textAnchor="middle" fontSize={9.5} fontWeight={600}
          fill="#ef4444" opacity={0.75} letterSpacing="0.1em">ACT NOW</text>
        <text x={SUN_CX} y={SUN_CY + R_MAX + 22} textAnchor="middle" fontSize={9.5} fontWeight={600}
          fill={t.c.textTertiary} letterSpacing="0.1em">MONITOR</text>

        {nodes.map((n) => (
          <line key={`ln-${n.id}`} x1={SUN_CX} y1={SUN_CY} x2={n.x} y2={n.y} stroke={sevColor(n.severity)}
            strokeWidth={n.id === selectedId ? 1.7 : 0.9} opacity={n.id === selectedId ? 0.55 : 0.16} />
        ))}

        <SunCore cx={SUN_CX} cy={SUN_CY} streaming={streaming} />

        {nodes.map((n, i) => {
          const color = sevColor(n.severity);
          const isSel = n.id === selectedId;
          return (
            <g key={n.id} onClick={() => onSelect(isSel ? null : n.id)} style={{ cursor: "pointer" }}>
              {n.severity >= 80 && (
                <circle cx={n.x} cy={n.y} r={n.size + 4} fill="none" stroke={color} strokeWidth={1.4}>
                  <animate attributeName="r" values={`${n.size + 3};${n.size + 13};${n.size + 3}`} dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.7;0;0.7" dur="2s" repeatCount="indefinite" />
                </circle>
              )}
              <motion.circle
                cx={n.x} cy={n.y} r={n.size} fill={color}
                initial={{ opacity: 0 }} animate={{ opacity: isSel ? 1 : 0.93 }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                style={{ filter: isSel ? `drop-shadow(0 0 12px ${color})` : `drop-shadow(0 2px 6px ${color}55)` }}
              />
              <circle cx={n.x - n.size * 0.28} cy={n.y - n.size * 0.3} r={n.size * 0.34} fill="#fff" opacity={0.22} />
              {isSel && <circle cx={n.x} cy={n.y} r={n.size + 6} fill="none" stroke={color} strokeWidth={1.8} opacity={0.75} />}
              <text x={n.x} y={n.y + 4} textAnchor="middle" fontSize={10.5} fontWeight={800} fill="#fff" pointerEvents="none">
                {n.severity}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Label cards — HTML so they stay readable, never truncated SVG text. */}
      {nodes.map((n) => {
        const color = sevColor(n.severity);
        const isSel = n.id === selectedId;
        const first = (n.person || "").split(" ")[0];
        const since = n.sinceLabel ? n.sinceLabel.split(",")[0] : null;
        return (
          <div key={`lb-${n.id}`} onClick={() => onSelect(isSel ? null : n.id)}
            style={{
              position: "absolute",
              left: `${Math.min(93, Math.max(7, (n.lx / SUN_W) * 100))}%`,
              top: `${Math.min(95, Math.max(4, (n.ly / SUN_H) * 100))}%`,
              transform: "translate(-50%, -50%)",
              maxWidth: 185, cursor: "pointer",
              background: t.c.surface, border: `1px solid ${isSel ? color : t.c.border}`,
              borderRadius: 10, padding: "6px 10px",
              boxShadow: isSel ? `0 4px 14px ${color}33` : t.shadow.sm,
              transition: "box-shadow .15s, border-color .15s",
            }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: t.c.text, lineHeight: 1.3, letterSpacing: "-0.01em" }}>
              {n.title}
            </div>
            <div style={{ fontSize: 10, color: t.c.textTertiary, marginTop: 2, whiteSpace: "nowrap" }}>
              {[first, since ? `since ${since}` : null, `${n.occurrences}×`].filter(Boolean).join(" · ")}
            </div>
          </div>
        );
      })}

      <div style={{
        position: "absolute", left: 14, bottom: 8, display: "flex", gap: 14, flexWrap: "wrap",
        fontSize: 10.5, color: t.c.textSecondary,
      }}>
        {[["#ef4444", "80+ act now"], ["#f97316", "60–79 this week"], ["#eab308", "40–59 monitor"]].map(([c2, l]) => (
          <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <i style={{ width: 8, height: 8, borderRadius: 99, background: c2 }} />{l}
          </span>
        ))}
      </div>
      <div style={{ position: "absolute", right: 14, bottom: 8, fontSize: 10.5, color: t.c.textTertiary }}>
        planet size = hours caught in the blocker
      </div>
    </div>
  );
}

// The proof panel for the selected blocker.
function BlockerDetail({ item, onClose }) {
  const t = useDashTheme();
  if (!item) {
    return (
      <div style={{ padding: t.s[5], display: "flex", flexDirection: "column", gap: t.s[3], color: t.c.textTertiary, fontSize: t.font.size.sm }}>
        <div style={{ fontSize: t.font.size.md, fontWeight: 650, color: t.c.textSecondary }}>How to read the map</div>
        <div>Every planet is a verified blocker — the same subject recurring across separate days in the tracked work. The closer it orbits the core, the more urgent it is.</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {[[85, "Act now — this week"], [65, "Handle this week"], [45, "Monitor"]].map(([s, l]) => (
            <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 99, background: sevColor(s), flexShrink: 0 }} />{l}
            </span>
          ))}
        </div>
        <div>Click any planet to see the exact problem, its evidence, the measured numbers behind it and the one next step.</div>
      </div>
    );
  }
  const color = sevColor(item.severity);
  return (
    <motion.div key={item.id} initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}
      style={{ padding: t.s[4], display: "flex", flexDirection: "column", gap: t.s[3] }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, color: "#fff", background: color }}>
          {sevLabel(item.severity)} · {item.severity}
        </span>
        <Badge tone="neutral">{item.category}</Badge>
        {item.person && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: t.font.size.xs, color: t.c.textSecondary, padding: "2px 8px 2px 3px", borderRadius: 99, border: `1px solid ${t.c.border}` }}>
            <Avatar name={item.person} size={16} />{item.person}
          </span>
        )}
        <button type="button" onClick={onClose} aria-label="Close details" style={{
          marginLeft: "auto", border: "none", background: "transparent", cursor: "pointer",
          color: t.c.textTertiary, fontSize: t.font.size.xs,
        }}>✕</button>
      </div>

      <div>
        <div style={{ fontSize: t.font.size.xl, fontWeight: 700, color: t.c.text, letterSpacing: "-0.015em", lineHeight: 1.25 }}>
          {item.title}
        </div>
        <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 3 }}>
          {[
            item.sinceLabel ? `since ${item.sinceLabel}` : null,
            `${item.occurrences} occurrences`,
            `${item.days} day${item.days === 1 ? "" : "s"}`,
            item.apps?.length ? item.apps.join(", ") : null,
          ].filter(Boolean).join(" · ")}
        </div>
      </div>

      <div style={{ height: 5, borderRadius: 99, background: t.c.surfaceMuted, overflow: "hidden" }}>
        <motion.div initial={{ width: 0 }} animate={{ width: `${item.severity}%` }} transition={{ duration: 0.5 }}
          style={{ height: "100%", borderRadius: 99, background: color }} />
      </div>

      <div style={{
        borderLeft: `3px solid ${color}`, background: color + "0d",
        borderRadius: `0 ${t.r.md}px ${t.r.md}px 0`, padding: t.s[3],
      }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
          The blocker
        </div>
        <p style={{ fontSize: t.font.size.sm, color: t.c.text, lineHeight: 1.6, margin: 0 }}>{item.problem}</p>
      </div>

      {item.evidence?.length > 0 && (
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Evidence</div>
          {item.evidence.map((e, i) => (
            <div key={i} style={{ display: "flex", gap: 8, fontSize: t.font.size.xs, color: t.c.textSecondary, lineHeight: 1.55, padding: "3px 0" }}>
              <span style={{ width: 5, height: 5, borderRadius: 99, background: color, flexShrink: 0, marginTop: 6 }} />{e}
            </div>
          ))}
        </div>
      )}

      {item.recommendation && (
        <div style={{ borderRadius: t.r.md, border: `1px solid ${color}44`, background: color + "0d", padding: t.s[3] }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 5 }}>
            <ChevronRight size={11} />Next step · {item.effort} effort
          </div>
          <div style={{ fontSize: t.font.size.sm, color: t.c.text, lineHeight: 1.55 }}>{item.recommendation}</div>
        </div>
      )}

      {/* Hard numbers, computed server-side — never written by the model. */}
      {item.facts?.length > 0 && item.facts.map((row) => (
        <div key={row.person} style={{ borderRadius: t.r.md, border: `1px solid ${t.c.border}`, overflow: "hidden" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 7, padding: `6px ${t.s[3]}px`,
            background: t.c.surfaceMuted, borderBottom: `1px solid ${t.c.border}`,
          }}>
            <Avatar name={row.person} size={16} />
            <span style={{ fontSize: t.font.size.xs, fontWeight: 650, color: t.c.text }}>{row.person}</span>
            <span style={{ marginLeft: "auto", fontSize: 10, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              measured
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(104px, 1fr))" }}>
            {row.facts.filter((f) => f.label !== "Seen by").map((f, i) => (
              <div key={f.label} style={{
                padding: `${t.s[2]}px ${t.s[3]}px`,
                borderTop: i > 1 ? `1px solid ${t.c.border}` : "none",
                borderLeft: i % 2 ? `1px solid ${t.c.border}` : "none",
              }}>
                <div style={{ fontSize: 10, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.05em" }}>{f.label}</div>
                <div style={{ fontSize: t.font.size.sm, fontWeight: 650, color: t.c.text, marginTop: 1, fontVariantNumeric: "tabular-nums" }}>{f.value}</div>
              </div>
            ))}
          </div>
          {row.byDay?.length > 1 && (() => {
            const max = Math.max(...row.byDay.map((d) => d.value), 0.1);
            return (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 30, padding: `6px ${t.s[3]}px`, borderTop: `1px solid ${t.c.border}` }}>
                {row.byDay.map((d) => (
                  <span key={d.date} title={`${d.date}: ${d.value}h`}
                    style={{
                      flex: 1, minWidth: 3, borderRadius: "2px 2px 0 0",
                      height: `${d.value ? Math.max(14, (d.value / max) * 100) : 6}%`,
                      background: d.value ? color : t.c.border, opacity: d.value ? 0.85 : 1,
                    }} />
                ))}
              </div>
            );
          })()}
        </div>
      ))}
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Graphical KPI tiles — every tile carries its own visual.
// ─────────────────────────────────────────────────────────────────────────────
function KpiShell({ accent, icon, label, info, infoSide, children }) {
  const t = useDashTheme();
  return (
    <div style={{
      position: "relative", padding: "12px 14px 11px", borderRadius: t.r.md,
      border: `1px solid ${t.c.border}`, background: t.c.surface, minWidth: 0,
    }}>
      <span style={{ position: "absolute", left: 0, top: 0, right: 0, height: 3, borderRadius: `${t.r.md}px ${t.r.md}px 0 0`, background: `linear-gradient(90deg, ${accent}, transparent)` }} />
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, color: t.c.textTertiary, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        <span style={{ display: "inline-flex", color: accent }}>{icon}</span>{label}
        {info && <InfoTip text={info} side={infoSide} />}
      </span>
      {children}
    </div>
  );
}

function KpiValue({ value, suffix, sub, subColor }) {
  const t = useDashTheme();
  return (
    <>
      <div style={{ fontSize: 21, fontWeight: 700, color: t.c.text, letterSpacing: "-0.02em", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
        {value ?? "—"}<span style={{ fontSize: 11, fontWeight: 500, color: t.c.textTertiary, marginLeft: 2 }}>{value != null ? suffix : ""}</span>
      </div>
      {sub && <div style={{ fontSize: 11, marginTop: 2, color: subColor || t.c.textTertiary }}>{sub}</div>}
    </>
  );
}

function KpiRow({ m, report, streaming }) {
  const t = useDashTheme();
  const k = m?.kpis || {};
  const trend = k.hoursTrendPct;
  const spark = m?.hoursByDay || [];
  const sparkMax = Math.max(...spark.map((d) => d.hours), 0.1);
  const blockers = report?.blockers;
  const quiet = m?.peopleSignals?.quiet || [];
  const flags = m?.period?.workdayFlags || [];
  const dayLetter = (d) => "SMTWTFS"[new Date(`${d}T12:00:00`).getDay()];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(196px, 1fr))", gap: t.s[2], marginBottom: t.s[4] }}>

      <KpiShell accent="#10b981" icon={<Clock size={11} />} label="Tracked hours"
        info="Total tracked hours for the period. The trend compares complete working days against the previous week; today and weekends are excluded. The mini bars are this period's daily hours (grey = non-working days).">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <KpiValue value={k.totalHours} suffix="h"
              sub={trend != null ? `${trend >= 0 ? "▲ +" : "▼ "}${trend}% vs last week` : "no full prior week to compare"}
              subColor={trend != null ? (trend >= 0 ? "#10b981" : "#f97316") : undefined} />
          </div>
          {spark.length > 1 && (
            <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 34, width: 86, flexShrink: 0 }} title="daily tracked hours">
              {spark.map((d) => (
                <span key={d.date} style={{
                  flex: 1, borderRadius: "2.5px 2.5px 0 0",
                  height: `${Math.max(10, (d.hours / sparkMax) * 100)}%`,
                  background: d.isWorkday ? "linear-gradient(180deg,#34d399,#10b981)" : t.c.surfaceMuted,
                  opacity: d.isWorkday ? 0.85 : 1,
                }} />
              ))}
            </div>
          )}
        </div>
      </KpiShell>

      <KpiShell accent={t.c.primary} icon={<Users size={11} />} label="People active"
        info="People with any tracked activity this period, out of those expected to track. The ring shows coverage. Anyone with no tracked data for 30+ days is treated as not expected to track and never counts against this number.">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <KpiValue value={k.peopleActive} suffix={` / ${k.peopleExpected ?? "—"}`}
              sub={m?.coverage?.notExpected?.count ? `${m.coverage.notExpected.count} not expected to track` : "expected trackers only"} />
          </div>
          <div style={{ position: "relative", width: 46, height: 46, flexShrink: 0 }}>
            <svg width="46" height="46" viewBox="0 0 46 46" style={{ transform: "rotate(-90deg)" }}>
              <circle cx="23" cy="23" r="19" fill="none" stroke={t.c.surfaceMuted} strokeWidth="5" />
              <circle cx="23" cy="23" r="19" fill="none" stroke={t.c.primary} strokeWidth="5" strokeLinecap="round"
                strokeDasharray={`${((k.coveragePct || 0) / 100) * 119} 119`} />
            </svg>
            <b style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: t.c.primary }}>
              {k.coveragePct != null ? `${k.coveragePct}%` : "—"}
            </b>
          </div>
        </div>
      </KpiShell>

      <KpiShell accent="#ef4444" icon={<AlertCircle size={11} />} label="Verified blockers"
        info="Things proven to be in the way of the work: the same failure, wait or stuck item recurring on 2 or more separate days. One bar per blocker — width and colour show severity, pulsing = act now. Zero means a clean week, not missing analysis.">
        <KpiValue value={blockers ? blockers.length : (streaming ? "…" : null)} suffix=""
          sub="proof required to appear" />
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 7, minHeight: 8 }}>
          {blockers && blockers.length === 0 && (
            <>
              <i style={{ width: 64, height: 8, borderRadius: 99, background: "#10b981", opacity: 0.85, display: "block" }} />
              <span style={{ fontSize: 10, color: "#10b981", fontWeight: 650 }}>all clear</span>
            </>
          )}
          {blockers && blockers.slice(0, 6).map((b) => (
            <i key={b.id} title={`${b.title} · ${b.severity}`} style={{
              width: 12 + (b.severity / 100) * 26, height: 8, borderRadius: 99,
              background: sevColor(b.severity), display: "block",
              animation: b.severity >= 80 ? "oivPulse 2s infinite" : "none",
            }} />
          ))}
          {!blockers && streaming && (
            <span style={{ fontSize: 10.5, color: t.c.textTertiary }}>verifying…</span>
          )}
        </div>
      </KpiShell>

      <KpiShell accent="#f97316" icon={<TrendingDown size={11} />} label="Went quiet"
        info="People who were active earlier in the period, then had nothing across the final 2+ working days. It describes the data trail, not performance — worth a check-in, not a conclusion.">
        <KpiValue value={k.wentQuiet} suffix="" sub="had data, then stopped" />
        {quiet.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", marginTop: 7 }}>
            {quiet.slice(0, 3).map((q) => (
              <span key={q.name} style={{ marginRight: -5, border: `2px solid ${t.c.surface}`, borderRadius: 99, display: "inline-flex" }}>
                <Avatar name={q.name} size={22} />
              </span>
            ))}
            <span style={{ display: "flex", gap: 3, marginLeft: 13, alignItems: "center" }} title="activity fading to nothing">
              {[1, 0.75, 0.45, 0, 0].map((o, i) => (
                <i key={i} style={{ width: 7, height: 7, borderRadius: 99, background: o ? "#f97316" : t.c.surfaceMuted, opacity: o || 1, display: "block" }} />
              ))}
            </span>
          </div>
        )}
      </KpiShell>

      <KpiShell accent="#8b5cf6" icon={<CalendarDays size={11} />} label="Working days"
        info="Days when the organisation was actually working, detected from its own activity volume (weekends and company holidays fall out naturally). Every average and trend on this page uses these days only." infoSide="bottomRight">
        <KpiValue value={k.workingDays} suffix={m?.period ? ` of ${m.period.daysInRange}` : ""} sub="detected from org activity" />
        {flags.length > 0 && flags.length <= 14 && (
          <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
            {flags.map((f) => (
              <span key={f.date} style={{ flex: 1, maxWidth: 26, textAlign: "center" }}>
                <i style={{ display: "block", height: 16, borderRadius: 5, background: f.isWorkday ? t.c.primary : t.c.surfaceMuted, opacity: f.isWorkday ? 0.85 : 1 }} />
                <em style={{ display: "block", fontStyle: "normal", fontSize: 8.5, color: t.c.textTertiary, marginTop: 3 }}>{dayLetter(f.date)}</em>
              </span>
            ))}
          </div>
        )}
      </KpiShell>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// People signals — pure measurement, no AI.
// ─────────────────────────────────────────────────────────────────────────────
function SignalCard({ color, title, info, children, note }) {
  const t = useDashTheme();
  return (
    <div style={{ borderRadius: t.r.lg, border: `1px solid ${t.c.border}`, background: t.c.surface, padding: `${t.s[3]}px ${t.s[4]}px`, boxShadow: t.shadow.sm }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color, marginBottom: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: 99, background: color }} />{title}
        {info && <InfoTip text={info} />}
      </div>
      {children}
      {note && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, paddingTop: 4 }}>{note}</div>}
    </div>
  );
}

function SignalRow({ name, why, num }) {
  const t = useDashTheme();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "5px 0", fontSize: t.font.size.xs }}>
      <Avatar name={name} size={18} />
      <span style={{ fontWeight: 650, color: t.c.text, whiteSpace: "nowrap" }}>{name}</span>
      <span style={{ color: t.c.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{why}</span>
      <span style={{ marginLeft: "auto", fontWeight: 650, color: t.c.text, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{num}</span>
    </div>
  );
}

function PeopleSignals({ signals }) {
  const t = useDashTheme();
  const quiet = signals?.quiet || [];
  const drops = signals?.drops || [];
  const overload = signals?.overload || [];
  if (!quiet.length && !drops.length && !overload.length) return null;
  return (
    <div style={{ marginTop: t.s[4] }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: t.s[2] }}>
        <span style={{ fontSize: t.font.size.md, fontWeight: 700, color: t.c.text }}>People signals</span>
        <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>
          computed from complete working days vs the person's own previous week — no AI involved
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: t.s[3] }}>
        {quiet.length > 0 && (
          <SignalCard color="#ef4444" title="Went quiet mid-week"
            info="Had tracked activity earlier in the period, then nothing across the final 2+ working days. Computed from raw activity, no AI. Usually a tracking or availability issue — check in before concluding anything.">
            {quiet.slice(0, 5).map((q) => (
              <SignalRow key={q.name} name={q.name} why={`active ${q.activeDays}d, nothing since`} num={`last: ${q.lastActiveLabel}`} />
            ))}
          </SignalCard>
        )}
        {drops.length > 0 && (
          <SignalCard color="#f97316" title="Sharp workload drop" note="today and weekends never trigger this"
            info="Tracked hours down 50% or more compared with the person's own previous week, on complete working days only. Needs at least 8 hours in the previous week, so new joiners and part-timers don't trigger it.">
            {drops.map((d) => (
              <SignalRow key={d.name} name={d.name} why={`${d.hours}h vs ${d.prevHours}h last week`} num={`${d.pct}%`} />
            ))}
          </SignalCard>
        )}
        {overload.length > 0 && (
          <SignalCard color="#8b5cf6" title="Overload risk"
            info="Averaging 9+ tracked hours per active day across 3 or more days. Sustained long days are a burnout signal — worth a conversation while it's still a pattern, not a problem.">
            {overload.map((o) => (
              <SignalRow key={o.name} name={o.name}
                why={`${o.perDay}h/day average${o.idleShare != null ? `, ${o.idleShare}% idle` : ""}`}
                num={`${o.hours}h`} />
            ))}
          </SignalCard>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Where the time went — measured tool hours, never capture counts.
// ─────────────────────────────────────────────────────────────────────────────
function TimeSpent({ timeSpent }) {
  const t = useDashTheme();
  if (!timeSpent?.available || !timeSpent.tools?.length) return null;
  const max = Math.max(...timeSpent.tools.map((x) => x.hours), 0.1);
  const commsMax = Math.max(...(timeSpent.commsPeople || []).map((x) => x.hours), 0.1);
  return (
    <div style={{ marginTop: t.s[4] }}>
      <SectionCard title="Where the time went"
        subtitle="Measured tool hours across the period — hours, never capture counts" icon={<Activity size={15} />}
        actions={<InfoTip side="bottomRight"
          text="Hours per application, measured from tracked activity — the honest answer to 'what do we actually do all day'. Amber bars are communication tools; the right column shows who spends the most time in them. This section only appears when measured app time is available." />}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)", gap: t.s[5], padding: t.s[4] }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: t.c.textTertiary, marginBottom: 8 }}>
              Top tools · {timeSpent.totalHours}h measured
            </div>
            {timeSpent.tools.map((x) => (
              <div key={x.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0", fontSize: t.font.size.xs }}>
                <span style={{ width: 118, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.name}</span>
                <div style={{ flex: 1, height: 8, borderRadius: 99, background: t.c.surfaceMuted, overflow: "hidden" }}>
                  <div style={{ width: `${(x.hours / max) * 100}%`, height: "100%", borderRadius: 99, background: x.category === "comms" ? "#f59e0b" : t.c.primary }} />
                </div>
                <span style={{ width: 48, textAlign: "right", fontWeight: 650, color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{x.hours}h</span>
                <span style={{ width: 58, textAlign: "right", fontSize: 10.5, color: t.c.textTertiary }}>{x.category}</span>
              </div>
            ))}
          </div>
          <div>
            {timeSpent.commsPeople?.length > 0 && (
              <>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: t.c.textTertiary, marginBottom: 8 }}>
                  Most time in communication
                </div>
                {timeSpent.commsPeople.map((x) => (
                  <div key={x.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0", fontSize: t.font.size.xs }}>
                    <Avatar name={x.name} size={16} />
                    <span style={{ width: 96, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.name}</span>
                    <div style={{ flex: 1, height: 8, borderRadius: 99, background: t.c.surfaceMuted, overflow: "hidden" }}>
                      <div style={{ width: `${(x.hours / commsMax) * 100}%`, height: "100%", borderRadius: 99, background: "#f59e0b" }} />
                    </div>
                    <span style={{ width: 44, textAlign: "right", fontWeight: 650, color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{x.hours}h</span>
                  </div>
                ))}
                <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 10, lineHeight: 1.5 }}>
                  Measured hours spent in communication tools across the period.
                </div>
              </>
            )}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Coverage — about the tracking, never the work. Quarantined by design.
// ─────────────────────────────────────────────────────────────────────────────
function CoverageBoard({ coverage }) {
  const t = useDashTheme();
  const stopped = coverage?.stopped || [];
  const notExpected = coverage?.notExpected;
  if (!stopped.length && !(notExpected?.count > 0)) return null;

  const Group = ({ color, bg, title, hint, info, count, children }) => (
    <div style={{ borderRadius: t.r.md, border: `1px solid ${t.c.border}`, background: t.c.surface }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 10px", borderBottom: `1px solid ${t.c.border}`, background: bg, borderRadius: `${t.r.md}px ${t.r.md}px 0 0` }}>
        <span style={{ width: 7, height: 7, borderRadius: 99, background: color, flexShrink: 0 }} />
        <span style={{ fontSize: t.font.size.xs, fontWeight: 700, color: t.c.text }}>{title}</span>
        {info && <InfoTip text={info} />}
        <span style={{ marginLeft: "auto", fontSize: 10.5, fontWeight: 700, color, background: color + "1f", padding: "1px 7px", borderRadius: 99, fontVariantNumeric: "tabular-nums" }}>{count}</span>
      </div>
      <div style={{ fontSize: 10, color: t.c.textTertiary, padding: "5px 10px 0" }}>{hint}</div>
      <div style={{ padding: "5px 10px 9px" }}>{children}</div>
    </div>
  );

  return (
    <div style={{ marginTop: t.s[4] }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: t.s[2] }}>
        <span style={{ fontSize: t.font.size.md, fontWeight: 700, color: t.c.text }}>Coverage</span>
        <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>
          about the tracking itself, never the work — kept out of blockers by design
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: t.s[3] }}>
        {stopped.length > 0 && (
          <Group color="#ef4444" bg="#ef444410" title="Stopped reporting" hint="tracked before, silent this period" count={stopped.length}
            info="These people had tracked data at some point in the last 30 days but nothing at all in this period. It's a tracking problem to fix on their machine — not a judgement about their work.">
            {stopped.slice(0, 6).map((s) => (
              <SignalRow key={s.name} name={s.name} why={s.position || ""} num={`last data ${s.lastDataDaysAgo}d ago`} />
            ))}
          </Group>
        )}
        {notExpected?.count > 0 && (
          <Group color={t.c.textTertiary} bg={t.c.surfaceMuted} title="Not expected to track" hint="no data for 30+ days — excluded from every percentage" count={notExpected.count}
            info="No tracked data for 30+ days — typically admins, founders or accounts that never had tracking installed. Excluded from coverage and every other percentage so they don't show up as permanent blind spots.">
            <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, padding: "4px 0", lineHeight: 1.5 }}>
              {notExpected.names?.slice(0, 6).join(", ")}{notExpected.names?.length > 6 ? ` +${notExpected.names.length - 6} more` : ""}
              — never counted as "blind spots".
            </div>
          </Group>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function OrgIntelligenceV2New() {
  const t = useDashTheme();
  const sf = useSurface();
  const { user } = useAuth();
  const stream = useAiStream("/api/v1/org-intelligence-v2/metrics-stream");

  const [member, setMember] = React.useState(null); // null = entire company
  const [includeTeam, setIncludeTeam] = React.useState(true);
  const [range, setRange] = React.useState(() => presetToRange("7d"));
  const [accessibleUsers, setAccessibleUsers] = React.useState([]);
  const [loadingUsers, setLoadingUsers] = React.useState(true);
  const [selectedId, setSelectedId] = React.useState(null);
  const [started, setStarted] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/saved-summaries?limit=1`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        if (json?.success && json.data?.accessibleUsers) setAccessibleUsers(json.data.accessibleUsers);
      } catch (_) {} finally { setLoadingUsers(false); }
    })();
  }, [user]);

  const members = React.useMemo(() => [
    { value: null, label: "Entire company" },
    ...accessibleUsers.map((u) => ({
      value: u._id || u.id,
      label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Member",
    })),
  ], [accessibleUsers]);

  const analyze = React.useCallback((presetKey = null, force = false) => {
    const r = presetKey ? rangeForPreset(presetKey) : range;
    if (presetKey) setRange(r);
    setStarted(true);
    setSelectedId(null);
    stream.run({
      userId: member || null,
      includeTeam: member ? includeTeam : false,
      startDate: fmt(r.start),
      endDate: fmt(r.end),
      forceRegenerate: force,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member, includeTeam, range]);

  const m = stream.metrics;
  const report = stream.report;
  const blockers = report?.blockers || [];
  const selected = blockers.find((b) => b.id === selectedId) || null;

  // Pre-select the most urgent blocker so the proof panel is never empty.
  React.useEffect(() => {
    if (report?.blockers?.length) setSelectedId(report.blockers[0].id);
  }, [report]);

  const health = report?.health?.score;
  const healthColor = health == null ? t.c.textTertiary : health >= 70 ? "#10b981" : health >= 45 ? "#f59e0b" : "#ef4444";
  const hp = report?.health?.parts;

  return (
    <PageShell
      eyebrow="AI · Organization"
      title="Org Intelligence"
      subtitle="Verified blockers, people signals and measured tool time — proven from your organisation's tracked activity"
      icon={<BrainCircuit size={22} />}
      topRight={
        <div className="tg-filterbar">
          <ToolSelect
            icon={<Users size={13} />}
            value={member}
            options={members}
            onChange={(v) => { setMember(v ?? null); }}
            placeholder={loadingUsers ? "Loading…" : "Entire company"}
            panelWidth={250}
            placement="bottomRight"
          />
          {member && (
            <>
              <span className="tg-tool-sep" />
              <button type="button" className="tg-tool" onClick={() => setIncludeTeam((x) => !x)}
                style={includeTeam ? { color: t.c.primary } : undefined}>
                <Check size={13} style={{ opacity: includeTeam ? 1 : 0.25 }} />
                <span className="tg-tool-value">Include team</span>
              </button>
            </>
          )}
          <span className="tg-tool-sep" />
          <DateRangeTool range={range} onChange={setRange} layoutId="companyBrainRangeChip"
            presets={[
              { key: "7d", label: "7 days" },
              { key: "30d", label: "30 days" },
              { key: "month", label: "This month" },
              { key: "lastMonth", label: "Last month" },
            ]}
          />
        </div>
      }
      actions={
        stream.streaming ? (
          <Button variant="secondary" leftIcon={<Square size={14} />} onClick={stream.stop}>Stop</Button>
        ) : (
          <Button variant="primary" leftIcon={<Zap size={15} />} onClick={() => analyze(null)}>Analyze</Button>
        )
      }
    >
      <style>{`@keyframes oivPulse{0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,.45)}50%{box-shadow:0 0 0 5px rgba(239,68,68,0)}}`}</style>
      <div style={canvasStyle(sf)}>
        <div style={ambientStyle(sf)} aria-hidden="true" />
        <div style={{ position: "relative" }}>
      {!started ? (
        <HeroSun onAnalyze={(preset) => analyze(preset)} />
      ) : (
        <>
          {/* ── Headline + computed health ── */}
          <div style={{
            display: "flex", alignItems: "center", gap: 14, marginBottom: t.s[4],
            padding: `${t.s[4]}px ${t.s[5]}px`, borderRadius: t.r.lg,
            border: `1px solid ${sf.cardBorder}`, background: sf.card, boxShadow: sf.cardShadow,
          }}>
            <div style={{ position: "relative", width: 52, height: 52, flexShrink: 0 }}>
              <svg viewBox="0 0 52 52" style={{ width: 52, height: 52, transform: "rotate(-90deg)" }}>
                <circle cx="26" cy="26" r="22" fill="none" stroke={t.c.surfaceMuted} strokeWidth="5" />
                {health != null && (
                  <motion.circle cx="26" cy="26" r="22" fill="none" stroke={healthColor} strokeWidth="5" strokeLinecap="round"
                    strokeDasharray={`${(health / 100) * 138} 138`} initial={{ strokeDasharray: "0 138" }}
                    animate={{ strokeDasharray: `${(health / 100) * 138} 138` }} transition={{ duration: 0.8 }} />
                )}
              </svg>
              <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: healthColor, fontVariantNumeric: "tabular-nums" }}>
                {health ?? "…"}
              </span>
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: t.font.size.lg, fontWeight: 650, color: t.c.text, letterSpacing: "-0.01em", lineHeight: 1.35 }}>
                {report?.headline
                  || (stream.streaming
                    ? (stream.status?.message || "Starting the analysis…")
                    : "Run an analysis to see the company read")}
              </div>
              {report?.summary && <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, marginTop: 2, lineHeight: 1.5 }}>{report.summary}</div>}
              {/* The score's own arithmetic, printed — never an AI opinion. */}
              {hp && (
                <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 5 }}>
                  Health {health} = coverage <b style={{ color: t.c.textSecondary }}>{hp.coveragePct}%</b>
                  {hp.trendPct != null && <> · hours vs last week <b style={{ color: t.c.textSecondary }}>{hp.trendPct > 0 ? "+" : ""}{hp.trendPct}%</b></>}
                  {" · "}verified blockers <b style={{ color: t.c.textSecondary }}>{hp.blockers}</b>
                  {" · "}gone quiet <b style={{ color: t.c.textSecondary }}>{hp.quiet}</b> — computed, not AI-scored
                  <InfoTip text="The health score is pure arithmetic over the parts shown here: coverage, hours versus last week, verified blockers and people gone quiet. The AI writes the headline sentence but never chooses this number, so the same data always gives the same score." />
                </div>
              )}
              {!report && stream.streaming && stream.status?.detail && (
                <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 5, flexWrap: "wrap" }}>
                  {stream.status.steps && (
                    <span style={{ display: "flex", gap: 3 }}>
                      {Array.from({ length: stream.status.steps }).map((_, i) => (
                        <i key={i} style={{
                          width: 16, height: 3, borderRadius: 2,
                          background: i < stream.status.step ? t.c.primary : t.c.border,
                          transition: "background .3s ease",
                        }} />
                      ))}
                    </span>
                  )}
                  <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                    {stream.status.detail}
                  </span>
                </div>
              )}
            </div>
            {stream.streaming && (
              <motion.span animate={{ rotate: 360 }} transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }}
                style={{ display: "inline-flex", color: t.c.primary, flexShrink: 0 }}><Sparkles size={18} /></motion.span>
            )}
          </div>

          {/* ── Graphical KPI row ── */}
          {m && <KpiRow m={m} report={report} streaming={stream.streaming} />}

          {stream.error && !report && (
            <EmptyState
              icon={<AlertCircle size={22} />}
              title={/no tracked activity/i.test(stream.error || "") ? "No activity found" : "Analysis unavailable"}
              description={(stream.error || "").replace(/\*\*/g, "")}
              action={!/no tracked activity/i.test(stream.error || "")
                ? <Button variant="primary" leftIcon={<Zap size={15} />} onClick={() => analyze(null)}>Retry</Button>
                : null}
            />
          )}

          {/* ── The blocker map ── */}
          {(blockers.length > 0 || stream.streaming || report?.allClear) && (
            <SectionCard
              title="Blocker map"
              subtitle="Every planet is a verified blocker — the same subject recurring on 2+ days. Click for proof."
              icon={<BrainCircuit size={15} />}
              actions={<InfoTip side="bottomRight"
                text="Each planet is one verified blocker. Distance from the core = urgency (closer means act sooner), planet size = hours caught in it, colour = severity. Click a planet or its label to see the exact problem, its evidence, the measured numbers and the one next step. A calm green sun means nothing recurred enough to qualify — a clean week." />}
            >
              {report?.allClear && !stream.streaming ? (
                <BlockerSun blockers={[]} allClear analysed={report.analysed} selectedId={null} onSelect={() => {}} streaming={false} />
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(280px, 2fr)", gap: t.s[3], padding: t.s[3], alignItems: "start" }}>
                  <div style={{ minWidth: 0 }}>
                    <BlockerSun blockers={blockers} selectedId={selectedId} onSelect={setSelectedId}
                      streaming={stream.streaming} allClear={false} analysed={report?.analysed} />
                  </div>
                  <div style={{ borderLeft: `1px solid ${t.c.border}`, minHeight: 340, position: "sticky", top: 0 }}>
                    <AnimatePresence mode="wait">
                      <BlockerDetail item={selected} onClose={() => setSelectedId(null)} />
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </SectionCard>
          )}

          {/* ── People signals ── */}
          {m && <PeopleSignals signals={m.peopleSignals} />}

          {/* ── Where the time went ── */}
          {m && <TimeSpent timeSpent={m.timeSpent} />}

          {/* ── Automation opportunities ── */}
          {report?.automations?.length > 0 && (
            <div style={{ marginTop: t.s[4] }}>
              <SectionCard title="Automation opportunities"
                subtitle="Manual work seen repeating on a schedule — hidden entirely when nothing qualifies"
                icon={<Workflow size={15} />}
                actions={<InfoTip side="bottomRight"
                  text="The same piece of work recurring in many separate hours across days for one person — a sign of a manual routine that a template, export or small script could remove. Only shown with a real observed frequency; if nothing repeats, this section doesn't appear." />}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))", gap: t.s[3], padding: t.s[3] }}>
                  {report.automations.map((a) => (
                    <div key={a.id} style={{
                      borderRadius: t.r.lg, border: `1px solid ${t.c.border}`, background: t.c.surface,
                      padding: t.s[4], boxShadow: t.shadow.sm, position: "relative", overflow: "hidden",
                    }}>
                      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: "#8b5cf6" }} />
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, color: "#8b5cf6", fontWeight: 650 }}>{a.frequency}</span>
                        {a.person && (
                          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, padding: "2px 8px 2px 3px", borderRadius: 99, background: t.c.surfaceMuted, color: t.c.textSecondary, border: `1px solid ${t.c.border}` }}>
                            <Avatar name={a.person} size={15} />{a.person}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: t.font.size.md, fontWeight: 700, color: t.c.text, letterSpacing: "-0.01em", lineHeight: 1.35 }}>{a.title}</div>
                      <p style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.6, margin: "6px 0 0" }}>{a.description}</p>
                      {a.tool && <div style={{ fontSize: 10.5, color: t.c.textTertiary, marginTop: 6 }}>in {a.tool}</div>}
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>
          )}

          {/* ── Coverage — quarantined ── */}
          {m && <CoverageBoard coverage={m.coverage} />}
        </>
      )}
        </div>
      </div>

      <AILimitModal isOpen={stream.limitReached} onClose={() => stream.setLimitReached(false)} />
    </PageShell>
  );
}
